import React, { useEffect, useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import DefaultEditor from 'react-simple-wysiwyg'
import { all_routes } from '../../router/all_routes'
import InstructorSidebar from '../common/instructorSidebar'
import { SubmitHandler, useForm } from 'react-hook-form'
import {
  additionalInformationFieldsConfig,
  AdditionalInformationFormValues,
  additionalInformationSchema,
  BasicInformationFormValues,
  basicInformationSchema,
  contentInformationFieldsConfig,
  CreateFormValues,
  initialFullFormValues,
  initialValuesStep1,
  initialValuesStep2,
  initialValuesStep3,
  initialValuesStep4,
  pricingFieldsConfig,
  PricingFormValues,
  parseCertificateMaterials,
  pricingSchema,
  questionInformationFieldsConfig,
  QuestionInformationFormValues,
  questionInformationSchema,
  transformPayload,
} from '../../../config/fields/liveTeaching'
import { yupResolver } from '@hookform/resolvers/yup'
import DynamicSelectFetcher from '../../../core/common/DynamicSelectFetcher'
import { DatePicker, Radio, Select, TimePicker } from 'antd'
import dayjs from 'dayjs'
import { useCourseList } from '../courses/hooks/useCourses'
import { useMutation } from '@tanstack/react-query'
import { postCourse } from '../../../api/courses'
import { useNotify } from '../../../provider/NotificationProvider'
import SelectIcon from '../common/SelectIcon'
import CertificateMaterialsInput from '../common/CertificateMaterialsInput'
import AddQuestionForm from '../cbt/components/QuestionForm'
import { normalizeLeadingInteger } from '../../../utils/numberNormalizer'

const LiveTeachingCreate = () => {
  const route = all_routes
  const notify = useNotify()
  const navigate = useNavigate()

  const [currentStep, setCurrentStep] = useState(1)
  const [allFormData, setAllFormData] = useState<CreateFormValues>(
    initialFullFormValues,
  )
  const [studiedItems, setStudiedItems] = useState<string[]>([''])
  const [crossSellItems, setCrossSellItems] = useState<
    Array<{
      cross_course_id: number | null
      note: string
      price: number | null
    }>
  >([])
  const [benefitsItems, setBenefitsItems] = useState<
    Array<{ icon: string; name: string }>
  >([])

  const [includedCourseType, setIncludedCourseType] = useState('all')

  const { data: courseListData, isLoading } = useCourseList({
    initialLimit: 100,
    type: 'all',
  })

  const {
    data: includedCourseData,
    isLoading: isIncludedCourseLoading,
    setSearch: setIncludedCourseSearch,
  } = useCourseList({
      initialLimit: 100,
      type: includedCourseType,
      status:
        includedCourseType === 'Live_Teaching' ? 'PUBLISHED' : undefined,
    })

  const optionsCourse = courseListData?.data?.map((course) => ({
    label: course.title,
    value: course.id,
  }))

  const optionsIncludedCourse = includedCourseData?.data?.map((course) => ({
    label: course.title,
    value: course.id,
  }))

  const buildIncludedCourseOptions = (
    _selectedIds: Array<number | string> | number | string | null | undefined = [],
  ) => {
    // Keep selected courses in the options so they can still be searched.
    // Ant Design Select already prevents duplicate values in multi-select mode.
    return optionsIncludedCourse || []
  }

  const {
    register: registerStep1,
    handleSubmit: handleSubmitStep1,
    formState: { errors: errorsStep1 },
    setValue: setValueStep1,
    watch: watchStep1,
    trigger: triggerStep1,
    getValues: getValuesStep1,
  } = useForm<BasicInformationFormValues>({
    resolver: yupResolver(basicInformationSchema),
    defaultValues: initialValuesStep1,
    mode: 'onBlur',
  })

  const {
    register: registerStep2,
    handleSubmit: handleSubmitStep2,
    formState: { errors: errorsStep2 },
    setValue: setValueStep2,
    watch: watchStep2,
    trigger: triggerStep2,
    getValues: getValuesStep2,
  } = useForm<QuestionInformationFormValues>({
    resolver: yupResolver(questionInformationSchema),
    defaultValues: initialValuesStep2,
    mode: 'onBlur', // Validate on blur
  })

  const {
    register: registerStep3,
    handleSubmit: handleSubmitStep3,
    formState: { errors: errorsStep3 },
    setValue: setValueStep3,
    watch: watchStep3,
    trigger: triggerStep3,
    getValues: getValuesStep3,
  } = useForm<PricingFormValues>({
    resolver: yupResolver(pricingSchema),
    defaultValues: initialValuesStep4,
    mode: 'onBlur',
  })

  const {
    register: registerStep4,
    handleSubmit: handleSubmitStep4,
    formState: { errors: errorsStep4 },
    setValue: setValueStep4,
    watch: watchStep4,
    trigger: triggerStep4,
    getValues: getValuesStep4,
    setError: setErrorStep4,
    clearErrors: clearErrorsStep4,
  } = useForm<AdditionalInformationFormValues>({
    resolver: yupResolver(additionalInformationSchema),
    defaultValues: initialValuesStep3,
    mode: 'onBlur', // Validate on blur
  })

  const isQuestionEnabled = watchStep2('has_questions') === 'yes'
  type CertificateRequiredField =
    | 'certificate_materials'
    | 'certificate_location'
  const requiredCertificateFieldKeys = [
    'certificate_materials',
    'certificate_location',
  ] as CertificateRequiredField[]

  const validateCertificateFields = () => {
    clearErrorsStep4(requiredCertificateFieldKeys)

    const values = getValuesStep4()
    let isValid = true

    if (!isQuestionEnabled) return isValid

    const requiredTextFields = [
      'certificate_location',
    ] as CertificateRequiredField[]

    requiredTextFields.forEach((field) => {
      if (!String(values[field] || '').trim()) {
        setErrorStep4(field, {
          type: 'manual',
          message: 'Field ini wajib diisi jika Quiz CBT aktif',
        })
        isValid = false
      }
    })

    const parsedMaterials = parseCertificateMaterials(values.certificate_materials)
    const materials = parsedMaterials.length
      ? parsedMaterials
      : studiedItems
          .map((topic) => ({ topic, jpl: '', speaker: '' }))
          .filter((item) => item.topic.trim())
    const hasCompleteMaterial = materials.some(
      (item) => item.topic,
    )

    if (!hasCompleteMaterial) {
      setErrorStep4('certificate_materials', {
        type: 'manual',
        message: 'Minimal satu Materi Sertifikat wajib diisi jika Quiz CBT aktif',
      })
      isValid = false
    }

    return isValid
  }

  const createMutation = useMutation({
    mutationFn: (data: any) => postCourse(data, true),
    onSuccess: () => {
      notify.success({
        message: 'Create berhasil',
        description: 'Data live teaching berhasil ditambahkan',
      })
      navigate(route.adminLiveTeaching)
    },
    onError: (err: any) => {
      let errorMessage = 'Terjadi kesalahan.'

      // Extract error message from API response
      if (err?.response?.data?.message) {
        errorMessage = err.response.data.message
      } else if (err?.response?.data?.error) {
        errorMessage = err.response.data.error
      } else if (err?.response?.data) {
        // Handle validation errors like {"slug":["The slug has already been taken."]}
        if (
          typeof err.response.data === 'object' &&
          !Array.isArray(err.response.data)
        ) {
          const errorMessages: string[] = []

          Object.entries(err.response.data).forEach(([field, messages]) => {
            if (Array.isArray(messages)) {
              messages.forEach((message: string) => {
                errorMessages.push(`${field}: ${message}`)
              })
            } else if (typeof messages === 'string') {
              errorMessages.push(`${field}: ${messages}`)
            }
          })

          errorMessage =
            errorMessages.length > 0
              ? errorMessages.join('\n')
              : JSON.stringify(err.response.data)
        } else if (typeof err.response.data === 'string') {
          errorMessage = err.response.data
        }
      } else if (err?.message) {
        errorMessage = err.message
      }

      notify.error({
        message: 'Create live teaching gagal',
        description: errorMessage,
      })
    },
  })

  useEffect(() => {
    if (studiedItems.length > 0) {
      setValueStep1('studied', studiedItems, {
        shouldValidate: true,
        shouldDirty: true,
      })
    } else {
      setValueStep1('studied', null, {
        shouldValidate: true,
        shouldDirty: true,
      })
    }
  }, [studiedItems, setValueStep1])

  const addNewStudiedItem = () => {
    setStudiedItems([...studiedItems, ''])
  }

  const updateStudiedItem = (index: number, value: number | string) => {
    const newItems: any = [...studiedItems]
    newItems[index] = value
    setStudiedItems(newItems)
  }

  const removeStudiedItem = (index: number) => {
    setStudiedItems(studiedItems.filter((_, i) => i !== index))
  }

  useEffect(() => {
    if (crossSellItems.length > 0) {
      setValueStep3('cross_sell', crossSellItems, {
        shouldValidate: true,
        shouldDirty: true,
      })
    } else {
      setValueStep3('cross_sell', null, {
        shouldValidate: true,
        shouldDirty: true,
      })
    }
  }, [crossSellItems, setValueStep3])

  const addNewCrossSellItem = () => {
    setCrossSellItems([
      ...crossSellItems,
      { cross_course_id: null, note: '', price: null },
    ])
  }

  const updateCrossSellItem = (
    index: number,
    field: 'cross_course_id' | 'note' | 'price',
    value: number | string,
  ) => {
    const newItems: any = [...crossSellItems]
    newItems[index][field] = value
    setCrossSellItems(newItems)
  }

  const removeCrossSellItem = (index: number) => {
    setCrossSellItems(crossSellItems.filter((_, i) => i !== index))
  }

  useEffect(() => {
    if (benefitsItems.length > 0) {
      setValueStep4('benefits', benefitsItems, {
        shouldValidate: true,
        shouldDirty: true,
      })
    } else {
      setValueStep4('benefits', null, {
        shouldValidate: true,
        shouldDirty: true,
      })
    }
  }, [benefitsItems, setValueStep4])

  useEffect(() => {
    if (!isQuestionEnabled) {
      setValueStep2('questions', [], {
        shouldValidate: true,
        shouldDirty: true,
      })
      setValueStep2('duration', null as any, {
        shouldValidate: false,
        shouldDirty: true,
      })
      setValueStep2('min_passing_score', '' as any, {
        shouldValidate: false,
        shouldDirty: true,
      })
      setValueStep2('max_attempt', '' as any, {
        shouldValidate: false,
        shouldDirty: true,
      })
    }
  }, [isQuestionEnabled, setValueStep2])

  const addNewBenefitItem = () => {
    setBenefitsItems([...benefitsItems, { icon: '', name: '' }])
  }

  const updateBenefitItem = (
    index: number,
    field: 'name' | 'icon',
    value: number | string,
  ) => {
    const newItems: any = [...benefitsItems]
    newItems[index][field] = value
    setBenefitsItems(newItems)
  }

  const removeBenefitItem = (index: number) => {
    setBenefitsItems(benefitsItems.filter((_, i) => i !== index))
  }

  const handleNextStep = async () => {
    let isValid = false
    let currentStepData: any = null

    if (currentStep === 1) {
      isValid = await triggerStep1()
      if (isValid) {
        currentStepData = getValuesStep1()
        setAllFormData((prev) => ({ ...prev, ...currentStepData }))
      }
    } else if (currentStep === 2) {
      isValid = await triggerStep2()
      if (isValid) {
        currentStepData = getValuesStep2()
        setAllFormData((prev) => ({ ...prev, ...currentStepData }))
      }
    } else if (currentStep === 4) {
      isValid = await triggerStep4()
      if (isValid) {
        currentStepData = getValuesStep4()
        setAllFormData((prev) => ({ ...prev, ...currentStepData }))
      }
    }

    if (isValid) {
      setCurrentStep(currentStep + 1)
    }
  }

  const handleSelectChange = (
    fieldName: keyof BasicInformationFormValues | keyof PricingFormValues,
    value: any,
    setFormValue: any,
  ) => {
    setFormValue(fieldName, value)
  }

  const handleEditorChange = (
    fieldName: keyof BasicInformationFormValues,
    value: string,
    setFormValue: any,
  ) => {
    setFormValue(fieldName, value, { shouldValidate: true, shouldDirty: true })
  }

  const handleDatePickerChange = (
    key: string,
    date: dayjs.Dayjs | null,
    setValueFn: typeof setValueStep1,
  ) => {
    setValueFn(key, date ? date.toDate() : null, {
      shouldValidate: true,
      shouldDirty: true,
    })
  }

  const handlePrevStep = () => {
    setCurrentStep(currentStep - 1)
  }

  const onSubmitFinal: SubmitHandler<any> = async () => {
    const isStepValid = await triggerStep3()

    if (!isStepValid) {
      return
    }

    // Update allFormData with the last step's data before constructing payload
    const finalStep4Data = getValuesStep3()

    const finalAllFormData = { ...allFormData, ...finalStep4Data }
    const dataFormatted = transformPayload(finalAllFormData)

    createMutation.mutate(dataFormatted)
  }

  return (
    <>
      <div className="content">
        <div className="container">
          <div className="row">
            <InstructorSidebar />
            <div className="col-lg-9 mx-auto">
              <div className="add-course-item">
                <div className="wizard">
                  <ul className="form-wizard-steps" id="progressbar2">
                    {[1, 2, 3, 4, 5].map((stepNum) => (
                      <li
                        key={stepNum}
                        className={
                          currentStep === stepNum
                            ? 'progress-active'
                            : currentStep > stepNum
                            ? 'progress-activated'
                            : ''
                        }
                      >
                        <div className="profile-step">
                          <span className="dot-active mb-2">
                            <span className="number">0{stepNum}</span>
                            <span className="tickmark">
                              <i className="fa-solid fa-check" />
                            </span>
                          </span>
                          <div className="step-section">
                            <p>
                              {stepNum === 1 && 'Content Information'}
                              {stepNum === 2 && 'Question & Answer'}
                              {stepNum === 3 && 'Sertifikat'}
                              {stepNum === 4 && 'Additional Information'}
                              {stepNum === 5 && 'Pricing'}
                            </p>
                          </div>
                        </div>
                      </li>
                    ))}
                  </ul>
                </div>
                <div className="initialization-form-set">
                  {currentStep === 1 && (
                    <form
                      onSubmit={handleSubmitStep1(handleNextStep)}
                      className="form-inner wizard-form-card"
                      id="first"
                    >
                      <div className="title">
                        <h5>Basic Information</h5>
                      </div>
                      <div className="row">
                        {Object.entries(contentInformationFieldsConfig).map(
                          ([key, fieldConfig]: any) => {
                            const isDynamicSelect =
                              fieldConfig.type === 'select' &&
                              fieldConfig.isSearchableAndInfinite
                            const isStaticSelect =
                              fieldConfig.type === 'select' &&
                              !fieldConfig.isSearchableAndInfinite

                            const error = errorsStep1[key]

                            const inputClassName = `form-control ${
                              error ? 'is-invalid' : ''
                            }`
                            const isFullWidth = [
                              'title',
                              'short_description',
                              'description',
                              'thumbnail',
                              'poster',
                              'studied',
                              'included_course_ids',
                              'is_favorite',
                            ].includes(key)

                            const isThreeColumn = [
                              'category_id',
                              'course_level',
                              'max_student',
                            ].includes(key)

                            const selectedIncludedIds = (watchStep1(key) || []) as Array<
                              number | string
                            >
                            const includedOptions = buildIncludedCourseOptions(
                              selectedIncludedIds,
                            )

                            return (
                              <div
                                className={`col-md-${
                                  isFullWidth ? '12' : isThreeColumn ? '4' : '6'
                                }`}
                                key={key}
                              >
                                <div className="input-block mb-3">
                                  {fieldConfig.type !== 'checkbox' && (
                                    <label className="form-label">
                                      {fieldConfig.label}
                                      {fieldConfig.required && (
                                        <span className="text-danger ms-1">
                                          *
                                        </span>
                                      )}
                                    </label>
                                  )}
                                  {(fieldConfig.type === 'text' ||
                                    fieldConfig.type === 'number' ||
                                    fieldConfig.type === 'url') && (
                                    <input
                                      type={
                                        fieldConfig.type === 'url'
                                          ? 'text'
                                          : fieldConfig.type
                                      }
                                      {...registerStep1(key, {
                                        valueAsNumber:
                                          fieldConfig.type === 'number',
                                      })}
                                      {...(fieldConfig.type === 'number'
                                        ? {
                                            value: normalizeLeadingInteger(
                                              watchStep1(key),
                                            ),
                                            onChange: (event: React.ChangeEvent<HTMLInputElement>) => {
                                          setValueStep1(
                                            key,
                                            normalizeLeadingInteger(event.target.value) as any,
                                            {
                                              shouldDirty: true,
                                              shouldValidate: true,
                                            },
                                          )
                                        },
                                      }
                                    : {})}
                                      className={inputClassName}
                                      placeholder={fieldConfig.placeholder}
                                    />
                                  )}
                                  {fieldConfig.type === 'textarea' && (
                                    <textarea
                                      {...registerStep1(key)}
                                      className={inputClassName}
                                      placeholder={fieldConfig.placeholder}
                                    ></textarea>
                                  )}

                                  {isDynamicSelect && (
                                    <DynamicSelectFetcher
                                      {...registerStep1(key)}
                                      fieldConfig={fieldConfig}
                                      value={watchStep1(key)}
                                      onChange={(selectedOption: any) => {
                                        handleSelectChange(
                                          key,
                                          selectedOption,
                                          setValueStep1,
                                        )
                                      }}
                                      placeholder={fieldConfig.placeholder}
                                    />
                                  )}
                                  {isStaticSelect && (
                                    <Select
                                      {...registerStep1(key)}
                                      options={fieldConfig?.options || []}
                                      className="select d-flex"
                                      style={{ width: '100%' }}
                                      placeholder={fieldConfig.placeholder}
                                      value={
                                        fieldConfig?.options?.find(
                                          (opt: any) =>
                                            opt.value === watchStep1(key),
                                        ) || null
                                      }
                                      onChange={(selectedOption: any) => {
                                        handleSelectChange(
                                          key,
                                          selectedOption,
                                          setValueStep1,
                                        )
                                      }}
                                    />
                                  )}

                                  {fieldConfig.type === 'multi-select' && (
                                    <>
                                      <div className="mb-2">
                                        <Select
                                          className="select d-flex"
                                          style={{ width: '100%' }}
                                          placeholder="Filter tipe course"
                                          value={includedCourseType}
                                          onChange={(value) =>
                                            setIncludedCourseType(value)
                                          }
                                          options={[
                                            { label: 'Semua', value: 'all' },
                                            { label: 'Course', value: 'Course' },
                                            { label: 'CBT', value: 'CBT' },
                                            {
                                              label: 'Live Teaching',
                                              value: 'Live_Teaching',
                                            },
                                          ]}
                                        />
                                      </div>
                                      <Select
                                        {...registerStep1(key)}
                                        mode="multiple"
                                        options={includedOptions}
                                        className="select d-flex"
                                        style={{ width: '100%' }}
                                        placeholder={fieldConfig.placeholder}
                                        showSearch
                                        filterOption={false}
                                        onSearch={setIncludedCourseSearch}
                                        value={watchStep1(key) || []}
                                        onChange={(selectedOption: any) =>
                                          handleSelectChange(
                                            key,
                                            selectedOption,
                                            setValueStep1,
                                          )
                                        }
                                        loading={isIncludedCourseLoading}
                                        allowClear
                                      />
                                    </>
                                  )}
                                  {fieldConfig.type === 'date' && (
                                    <DatePicker
                                      {...registerStep1(key)}
                                      ref={registerStep1(key).ref} // Penting untuk meneruskan ref
                                      className={`${inputClassName} w-100`} // Terapkan styling
                                      placeholder={fieldConfig.placeholder}
                                      value={
                                        watchStep1(key)
                                          ? dayjs(watchStep1(key) as any)
                                          : null
                                      } // Konversi Date object ke dayjs
                                      onChange={(date, dateString) =>
                                        handleDatePickerChange(
                                          key,
                                          date,
                                          setValueStep1,
                                        )
                                      }
                                      showTime
                                      format={
                                        fieldConfig.format ||
                                        'DD-MM-YYYY hh:mm A'
                                      }
                                      status={error ? 'error' : undefined}
                                    />
                                  )}
                                  {fieldConfig.type === 'richtext' && (
                                    <div className="summernote">
                                      <DefaultEditor
                                        value={
                                          (watchStep1(key) as string) || ''
                                        }
                                        onChange={(e) =>
                                          handleEditorChange(
                                            key,
                                            e.target.value,
                                            setValueStep1,
                                          )
                                        }
                                      />
                                    </div>
                                  )}
                                  {fieldConfig.type === 'array' && (
                                    <>
                                      {studiedItems.length === 0 && (
                                        <div className="alert alert-info text-center">
                                          No Data added.
                                        </div>
                                      )}
                                      {studiedItems.map((item, index) => {
                                        return (
                                          <div
                                            key={index}
                                            className="d-flex align-items-center mb-2"
                                          >
                                            <div className="flex-grow-1 me-2">
                                              <input
                                                type="text"
                                                className="form-control w-100"
                                                placeholder="(e.g., Studi kasus terbaru)"
                                                value={item}
                                                onChange={(e) =>
                                                  updateStudiedItem(
                                                    index,
                                                    e.target.value,
                                                  )
                                                }
                                              />
                                            </div>
                                            <Link
                                              to="#"
                                              className="link-trash delete-btn1"
                                              onClick={() =>
                                                removeStudiedItem(index)
                                              }
                                            >
                                              <i className="isax isax-trash fs-16" />
                                            </Link>
                                          </div>
                                        )
                                      })}

                                      <div className="d-flex align-items-center justify-content-end">
                                        <Link
                                          to="#"
                                          className="d-flex align-items-center add-new-topic"
                                          id="add-new-topic-btn"
                                          onClick={addNewStudiedItem}
                                        >
                                          <i className="isax isax-add me-1" />{' '}
                                          Add New Data
                                        </Link>
                                        <div></div>
                                      </div>
                                    </>
                                  )}
                                  {fieldConfig.type === 'file' && (
                                    <div className="row align-items-center">
                                      <div className="col-md-10">
                                        <input
                                          type="text"
                                          className={inputClassName}
                                          placeholder="No File Selected"
                                          readOnly
                                          value={
                                            watchStep1(key)?.[0]?.name || ''
                                          }
                                        />
                                      </div>
                                      <div className="col-md-2 d-grid">
                                        <label
                                          htmlFor={`${key}-upload`}
                                          className="file-upload-btn text-center"
                                        >
                                          Upload File
                                        </label>
                                        <input
                                          type="file"
                                          id={`${key}-upload`}
                                          {...registerStep1(key)}
                                          className="d-none" // Hide default file input
                                        />
                                      </div>
                                    </div>
                                  )}
                                  {fieldConfig.type === 'checkbox' && (
                                    <div className="d-flex align-items-center gap-3 p-3 rounded-3 border bg-light mt-2 mb-2">
                                      <div className="form-check form-switch form-check-md m-0 p-0 d-flex align-items-center">
                                        <input
                                          type="checkbox"
                                          id={key}
                                          checked={Boolean(watchStep1(key))}
                                          onChange={(e) =>
                                            setValueStep1(key, e.target.checked, {
                                              shouldDirty: true,
                                              shouldValidate: true,
                                            })
                                          }
                                          className="form-check-input ms-0 cursor-pointer"
                                          style={{ width: '46px', height: '24px', cursor: 'pointer' }}
                                        />
                                        <label
                                          className="form-check-label fw-bold text-dark cursor-pointer ms-3 mb-0"
                                          htmlFor={key}
                                          style={{ cursor: 'pointer', fontSize: '14px' }}
                                        >
                                          {fieldConfig.label}
                                        </label>
                                      </div>
                                    </div>
                                  )}

                                  {error && (
                                    <div className="text-danger">
                                      {error.message}
                                    </div>
                                  )}
                                </div>
                              </div>
                            )
                          },
                        )}
                      </div>
                      <div className="add-form-btn widget-next-btn submit-btn d-flex justify-content-end mb-0">
                        <div className="btn-left">
                          <button
                            type="submit"
                            className="btn main-btn next_btns"
                          >
                            Next <i className="isax isax-arrow-right-3 ms-1" />
                          </button>
                        </div>
                      </div>
                    </form>
                  )}

                  {currentStep === 2 && (
                    <form
                      onSubmit={handleSubmitStep2(handleNextStep)}
                      className="form-inner wizard-form-card"
                      id="second"
                    >
                      <div className="title">
                        <h5>Question & Answer</h5>
                      </div>
                      <div className="row">
                        <div className="col-md-12">
                          <div className="input-block">
                            <label className="form-label">
                              {questionInformationFieldsConfig.has_questions
                                .label}
                              {questionInformationFieldsConfig.has_questions
                                .required && (
                                <span className="text-danger ms-1">*</span>
                              )}
                            </label>
                            <Radio.Group
                              value={watchStep2('has_questions')}
                              onChange={(event) =>
                                setValueStep2(
                                  'has_questions',
                                  event.target.value,
                                  {
                                    shouldValidate: true,
                                    shouldDirty: true,
                                  },
                                )
                              }
                            >
                              {questionInformationFieldsConfig.has_questions.options?.map(
                                (option) => (
                                  <Radio key={option.value} value={option.value}>
                                    {option.label}
                                  </Radio>
                                ),
                              )}
                            </Radio.Group>
                            {errorsStep2.has_questions && (
                              <div className="text-danger">
                                {errorsStep2.has_questions.message}
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                      {isQuestionEnabled ? (
                        <div className="row">
                          <div className="col-md-12">
                            <div className="input-block">
                              <label className="form-label">
                                {questionInformationFieldsConfig.duration.label}
                                {questionInformationFieldsConfig.duration
                                  .required && (
                                  <span className="text-danger ms-1">*</span>
                                )}
                              </label>
                              <TimePicker
                                {...registerStep2('duration')}
                                ref={registerStep2('duration').ref}
                                className={`form-control w-100 ${
                                  errorsStep2.duration ? 'is-invalid' : ''
                                }`}
                                placeholder={
                                  questionInformationFieldsConfig.duration
                                    .placeholder
                                }
                                value={
                                  watchStep2('duration')
                                    ? dayjs(watchStep2('duration') as any)
                                    : null
                                }
                                onChange={(date) =>
                                  setValueStep2('duration', date as any, {
                                    shouldValidate: true,
                                    shouldDirty: true,
                                  })
                                }
                                format="HH:mm"
                                status={
                                  errorsStep2.duration ? 'error' : undefined
                                }
                              />
                              {errorsStep2.duration && (
                                <div className="text-danger">
                                  {errorsStep2.duration.message as string}
                                </div>
                              )}
                            </div>
                          </div>
                          <div className="col-md-6">
                            <div className="input-block">
                              <label className="form-label">
                                {
                                  questionInformationFieldsConfig
                                    .min_passing_score.label
                                }
                                {questionInformationFieldsConfig
                                  .min_passing_score.required && (
                                  <span className="text-danger ms-1">*</span>
                                )}
                              </label>
                              <input
                                type="number"
                                {...registerStep2('min_passing_score')}
                                value={normalizeLeadingInteger(
                                  watchStep2('min_passing_score'),
                                )}
                                onChange={(event) =>
                                  setValueStep2(
                                    'min_passing_score',
                                    normalizeLeadingInteger(event.target.value) as any,
                                    {
                                      shouldDirty: true,
                                      shouldValidate: true,
                                    },
                                  )
                                }
                                className={`form-control ${
                                  errorsStep2.min_passing_score
                                    ? 'is-invalid'
                                    : ''
                                }`}
                                placeholder={
                                  questionInformationFieldsConfig
                                    .min_passing_score.placeholder
                                }
                              />
                              {errorsStep2.min_passing_score && (
                                <div className="text-danger">
                                  {
                                    errorsStep2.min_passing_score
                                      .message as string
                                  }
                                </div>
                              )}
                            </div>
                          </div>
                          <div className="col-md-6">
                            <div className="input-block">
                              <label className="form-label">
                                {questionInformationFieldsConfig.max_attempt
                                  .label}
                                {questionInformationFieldsConfig.max_attempt
                                  .required && (
                                  <span className="text-danger ms-1">*</span>
                                )}
                              </label>
                              <input
                                type="number"
                                {...registerStep2('max_attempt')}
                                value={normalizeLeadingInteger(
                                  watchStep2('max_attempt'),
                                )}
                                onChange={(event) =>
                                  setValueStep2(
                                    'max_attempt',
                                    normalizeLeadingInteger(event.target.value) as any,
                                    {
                                      shouldDirty: true,
                                      shouldValidate: true,
                                    },
                                  )
                                }
                                className={`form-control ${
                                  errorsStep2.max_attempt ? 'is-invalid' : ''
                                }`}
                                placeholder={
                                  questionInformationFieldsConfig.max_attempt
                                    .placeholder
                                }
                              />
                              {errorsStep2.max_attempt && (
                                <div className="text-danger">
                                  {errorsStep2.max_attempt.message as string}
                                </div>
                              )}
                            </div>
                          </div>
                          <div className="col-md-12">
                            <AddQuestionForm
                              name="questions"
                              register={registerStep2 as any}
                              errors={errorsStep2 as any}
                              setValue={setValueStep2 as any}
                              watch={watchStep2 as any}
                            />
                            {errorsStep2.questions && (
                              <div className="text-danger mt-2">
                                {errorsStep2.questions.message as string}
                              </div>
                            )}
                          </div>
                        </div>
                      ) : (
                        <div className="alert alert-info mt-3">
                          Question & Answer tidak diaktifkan.
                        </div>
                      )}
                      <div className="add-form-btn widget-next-btn submit-btn">
                        <div className="btn-left">
                          <button
                            type="button"
                            className="btn btn-light main-btn prev_btns"
                            onClick={handlePrevStep}
                          >
                            <i className="isax isax-arrow-left-2 me-1" /> Prev
                          </button>
                        </div>
                        <div className="btn-left">
                          <button
                            type="submit"
                            className="btn btn-secondary main-btn next_btns"
                          >
                            Next <i className="isax isax-arrow-right-3 ms-1" />
                          </button>
                        </div>
                      </div>
                    </form>
                  )}

                  {(currentStep === 3 || currentStep === 4) && (
                    <form
                      onSubmit={
                        currentStep === 3
                          ? (event) => {
                              event.preventDefault()
                              if (!validateCertificateFields()) return
                              setAllFormData((prev) => ({
                                ...prev,
                                ...getValuesStep4(),
                              } as CreateFormValues))
                              setCurrentStep(4)
                            }
                          : handleSubmitStep4(handleNextStep)
                      }
                      className="form-inner wizard-form-card"
                      style={{ display: 'block' }}
                    >
                      <div className="title">
                        <h5>
                          {currentStep === 3
                            ? 'Sertifikat'
                            : 'Additional Information'}
                        </h5>
                      </div>
                      <div className="row">
                        {(() => {
                          const certificateFieldKeys = new Set([
                            'certificate_event_dates',
                            'certificate_jpl_total',
                            'certificate_jpl_note',
                            'certificate_materials',
                            'certificate_location',
                            'certificate_signature_date',
                          ])
                          const filteredFields = Object.entries(
                            additionalInformationFieldsConfig,
                          ).filter(([key]) =>
                            currentStep === 3
                              ? certificateFieldKeys.has(key)
                              : !certificateFieldKeys.has(key),
                          )

                          return filteredFields.map(([key, fieldConfig]) => {
                            const error =
                              errorsStep4[
                                key as keyof AdditionalInformationFormValues
                              ]
                            const isCertificateFieldRequired =
                              currentStep === 3 &&
                              isQuestionEnabled &&
                              requiredCertificateFieldKeys.includes(
                                key as CertificateRequiredField,
                              )
                            const inputClassName = `form-control ${
                              error ? 'is-invalid' : ''
                            }`

                            if (
                              fieldConfig.type === 'text' ||
                              fieldConfig.type === 'url'
                            ) {
                              return (
                                <div className="col-md-12" key={key}>
                                  <div className="input-block">
                                    <label className="form-label">
                                      {fieldConfig.label}
                                      {(fieldConfig.required ||
                                        isCertificateFieldRequired) && (
                                        <span className="text-danger ms-1">
                                          *
                                        </span>
                                      )}
                                    </label>
                                    <input
                                      type={
                                        fieldConfig.type === 'url'
                                          ? 'text'
                                          : fieldConfig.type
                                      }
                                      {...registerStep4(key)}
                                      className={inputClassName}
                                      placeholder={
                                        'placeholder' in fieldConfig
                                          ? fieldConfig.placeholder
                                          : undefined
                                      }
                                    />
                                    {error && (
                                      <div className="text-danger">
                                        {error.message}
                                      </div>
                                    )}
                                  </div>
                                </div>
                              )
                            }

                            if (fieldConfig.type === 'textarea') {
                              return (
                                <div className="col-md-12" key={key}>
                                  <div className="input-block">
                                    <label className="form-label">
                                      {fieldConfig.label}
                                      {(fieldConfig.required ||
                                        isCertificateFieldRequired) && (
                                        <span className="text-danger ms-1">
                                          *
                                        </span>
                                      )}
                                    </label>
                                    <textarea
                                      {...registerStep4(key)}
                                      className={inputClassName}
                                      rows={key === 'certificate_materials' ? 5 : 3}
                                      placeholder={
                                        'placeholder' in fieldConfig
                                          ? fieldConfig.placeholder
                                          : undefined
                                      }
                                    />
                                    {error && (
                                      <div className="text-danger">
                                        {error.message}
                                      </div>
                                    )}
                                  </div>
                                </div>
                              )
                            }

                            if (fieldConfig.type === 'certificate_materials') {
                              return (
                                <div className="col-md-12" key={key}>
                                  <div className="input-block">
                                    <label className="form-label">
                                      {fieldConfig.label}
                                      {isCertificateFieldRequired && (
                                        <span className="text-danger ms-1">
                                          *
                                        </span>
                                      )}
                                    </label>
                                    <CertificateMaterialsInput
                                      value={(watchStep4(key) as string) || ''}
                                      fallbackTopics={studiedItems}
                                      onChange={(value) =>
                                        setValueStep4(key as any, value as any)
                                      }
                                    />
                                    {error && (
                                      <div className="text-danger">
                                        {error.message}
                                      </div>
                                    )}
                                  </div>
                                </div>
                              )
                            }

                            if (fieldConfig.type === 'array') {
                              return (
                                <div className="col-md-12 mt-4" key={key}>
                                  <div className="bg-light pt-3 rounded-3 p-3">
                                    <h6 className="mb-2">Benefits</h6>
                                    <button
                                      type="button"
                                      className="btn btn-sm btn-primary mb-3"
                                      onClick={addNewBenefitItem}
                                    >
                                      Add New Benefit
                                    </button>
                                    {benefitsItems.length === 0 && (
                                      <div className="alert alert-info text-center">
                                        No benefits added.
                                      </div>
                                    )}
                                    {benefitsItems.map((item, index) => {
                                      const data: any = watchStep4?.(
                                        'benefits',
                                      )?.[index]

                                      const value =
                                        additionalInformationFieldsConfig?.benefits?.options?.find(
                                          (opt) => opt.value === data?.icon,
                                        ) || null
                                      return (
                                        <div
                                          key={index}
                                          className="d-flex align-items-center mb-2"
                                        >
                                          <div className="me-2">
                                            <SelectIcon
                                              {...registerStep4(key)}
                                              style={{ width: '5rem' }}
                                              className="select"
                                              placeholder="Select Icon Benefit"
                                              value={value}
                                              onChange={(value: any) => {
                                                updateBenefitItem(
                                                  index,
                                                  'icon',
                                                  value,
                                                )
                                              }}
                                              allowClear
                                            />
                                          </div>
                                          <div className="flex-grow-1 me-2">
                                            <input
                                              type="text"
                                              className="form-control w-100"
                                              placeholder="Nama Benefit"
                                              value={item.name}
                                              onChange={(e) =>
                                                updateBenefitItem(
                                                  index,
                                                  'name',
                                                  e.target.value,
                                                )
                                              }
                                            />
                                          </div>
                                          <Link
                                            to="#"
                                            className="link-trash delete-btn1"
                                            onClick={() =>
                                              removeBenefitItem(index)
                                            }
                                          >
                                            <i className="isax isax-trash fs-16" />
                                          </Link>
                                        </div>
                                      )
                                    })}
                                    {error && (
                                      <div className="text-danger">
                                        {error.message}
                                      </div>
                                    )}
                                  </div>
                                </div>
                              )
                            }

                            return null
                          })
                        })()}
                      </div>
                      <div className="add-form-btn widget-next-btn submit-btn">
                        <div className="btn-left">
                          <button
                            type="button"
                            className="btn btn-light main-btn prev_btns"
                            onClick={handlePrevStep}
                          >
                            <i className="isax isax-arrow-left-2 me-1" /> Prev
                          </button>
                        </div>
                        <div className="btn-left">
                          <button
                            type="submit"
                            className="btn btn-secondary main-btn next_btns"
                          >
                            Next <i className="isax isax-arrow-right-3 ms-1" />
                          </button>
                        </div>
                      </div>
                    </form>
                  )}

                  {currentStep === 5 && (
                    <form
                      onSubmit={handleSubmitStep3(onSubmitFinal)}
                      className="form-inner wizard-form-card"
                      style={{ display: 'block' }}
                    >
                      <div className="title">
                        <h5>Pricing</h5>
                      </div>
                      <div className="row">
                        {Object.entries(pricingFieldsConfig).map(
                          ([key, fieldConfig]: any) => {
                            const error = errorsStep3[key]
                            const inputClassName = `form-control ${
                              error ? 'is-invalid' : ''
                            }`

                            const isDiscountField =
                              key === 'discount_type' || key === 'discount'
                            const isDisabled =
                              isDiscountField && watchStep3('price') === 0

                            return (
                              <div className="col-md-12" key={key}>
                                <div className="input-block">
                                  <label className="form-label">
                                    {fieldConfig.label}
                                    {fieldConfig.required && (
                                      <span className="text-danger ms-1">
                                        *
                                      </span>
                                    )}
                                  </label>
                                  {(fieldConfig.type === 'text' ||
                                    fieldConfig.type === 'number') && (
                                    <input
                                      type={fieldConfig.type}
                                      {...registerStep3(key, {
                                        valueAsNumber:
                                          fieldConfig.type === 'number',
                                      })}
                                      className={inputClassName}
                                      placeholder={fieldConfig.placeholder}
                                      disabled={isDisabled}
                                    />
                                  )}
                                  {fieldConfig.type === 'select' && (
                                    <Select
                                      {...registerStep3(key)}
                                      options={fieldConfig?.options || []}
                                      className="select d-flex"
                                      placeholder={fieldConfig.placeholder}
                                      value={
                                        fieldConfig.options?.find(
                                          (opt: any) =>
                                            opt.value === watchStep3(key),
                                        ) || null
                                      }
                                      onChange={(selectedOption: any) =>
                                        handleSelectChange(
                                          key,
                                          selectedOption,
                                          setValueStep3,
                                        )
                                      }
                                      disabled={isDisabled}
                                    />
                                  )}

                                  {fieldConfig.type === 'array' && (
                                    <>
                                      {crossSellItems.length === 0 && (
                                        <div className="alert alert-info text-center">
                                          No cross-sell products added.
                                        </div>
                                      )}
                                      {crossSellItems.map((item, index) => {
                                        const data: any = watchStep3?.(key)?.[
                                          index
                                        ]

                                        const value =
                                          optionsCourse?.find(
                                            (opt: any) =>
                                              opt.value?.toString() ===
                                              data?.cross_course_id?.toString(),
                                          ) || null
                                        return (
                                          <div
                                            key={index}
                                            className="d-flex align-items-center mb-2"
                                          >
                                            <div
                                              className="flex-grow-1 me-2"
                                              style={{ maxWidth: '30vw' }}
                                            >
                                              <Select
                                                {...registerStep3(key)}
                                                options={optionsCourse || []}
                                                className="select w-100"
                                                placeholder={
                                                  fieldConfig.placeholder
                                                }
                                                loading={isLoading}
                                                value={value}
                                                onChange={(
                                                  selectedOption: any,
                                                ) =>
                                                  updateCrossSellItem(
                                                    index,
                                                    'cross_course_id',
                                                    selectedOption,
                                                  )
                                                }
                                              />
                                            </div>
                                            <div className="flex-grow-1 me-2">
                                              <input
                                                type="text"
                                                className="form-control w-100"
                                                placeholder="Note (e.g., discount for this product)"
                                                value={item.note}
                                                onChange={(e) =>
                                                  updateCrossSellItem(
                                                    index,
                                                    'note',
                                                    e.target.value,
                                                  )
                                                }
                                              />
                                            </div>
                                            <div className="flex-grow-1 me-2">
                                              <input
                                                type="number"
                                                className="form-control w-100"
                                                placeholder="Price *"
                                                value={item.price || ''}
                                                onChange={(e) =>
                                                  updateCrossSellItem(
                                                    index,
                                                    'price',
                                                    e.target.value === ''
                                                      ? null
                                                      : Number(e.target.value),
                                                  )
                                                }
                                                required
                                              />
                                            </div>
                                            <Link
                                              to="#"
                                              className="link-trash delete-btn1"
                                              onClick={() =>
                                                removeCrossSellItem(index)
                                              }
                                            >
                                              <i className="isax isax-trash fs-16" />
                                            </Link>
                                          </div>
                                        )
                                      })}

                                      <div className="d-flex align-items-center justify-content-end">
                                        <Link
                                          to="#"
                                          className="d-flex align-items-center add-new-topic"
                                          id="add-new-topic-btn"
                                          onClick={addNewCrossSellItem}
                                        >
                                          <i className="isax isax-add me-1" />{' '}
                                          Add New Cross-Sell Product
                                        </Link>
                                        <div></div>
                                      </div>
                                    </>
                                  )}
                                  {error && (
                                    <div className="text-danger">
                                      {error.message}
                                    </div>
                                  )}
                                </div>
                              </div>
                            )
                          },
                        )}
                      </div>
                      <div className="add-form-btn widget-next-btn submit-btn">
                        <div className="btn-left">
                          <button
                            type="button"
                            className="btn btn-light main-btn prev_btns"
                            onClick={handlePrevStep}
                            disabled={createMutation.isPending}
                          >
                            <i className="isax isax-arrow-left-2 me-1" /> Prev
                          </button>
                        </div>
                        <div className="btn-left">
                          <button
                            type="submit"
                            className="btn btn-success main-btn"
                            disabled={createMutation.isPending}
                          >
                            Submit
                          </button>
                        </div>
                      </div>
                    </form>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  )
}

export default LiveTeachingCreate
